import React, { useState, useEffect } from 'react';
import { BarChart3, Search, TrendingUp, Users, Eye, Clock, MousePointerClick, ArrowUpRight, ArrowDownRight, Lock, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { Environment } from '../types';
import EmptyState from '../components/EmptyState';
import { toast } from '../components/Toast';

interface AnalyticsProps {
  activeEnv: Environment | null;
}

type TabKey = 'overview' | 'pageSearch';

interface OverviewData {
  kpis: Record<string, number>;
  trend: Array<Record<string, any>>;
  topPages: Array<Record<string, any>>;
  days: number;
}

interface PageSearchData {
  query: string;
  matches: Array<Record<string, any>>;
  siteAverage: Record<string, number>;
  trend: Array<Record<string, any>>;
  days: number;
}

const TABS: { key: TabKey; label: string; icon: any }[] = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'pageSearch', label: 'Page Search', icon: Search },
];

const Analytics: React.FC<AnalyticsProps> = ({ activeEnv }) => {
  const [tab, setTab] = useState<TabKey>('overview');
  const [days, setDays] = useState(30);
  const [status, setStatus] = useState<{ connected: boolean; measurementId?: string | null; propertyId?: string | null } | null>(null);
  const [statusChecked, setStatusChecked] = useState(false);

  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const [pageQuery, setPageQuery] = useState('');
  const [pageResult, setPageResult] = useState<PageSearchData | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  // Check GA4 connection status on mount + env change
  useEffect(() => {
    if (!activeEnv) {
      setStatus(null);
      setStatusChecked(false);
      return;
    }
    const run = async () => {
      try {
        const resp = await fetch(
          `/api/analytics/ga4?environmentId=${activeEnv.id}&action=status`,
          { credentials: 'include' }
        );
        if (resp.ok) {
          const data = await resp.json();
          setStatus(data);
        } else {
          setStatus({ connected: false });
        }
      } catch {
        setStatus({ connected: false });
      } finally {
        setStatusChecked(true);
      }
    };
    run();
  }, [activeEnv?.id]);

  // Fetch overview whenever tab, env, or days change
  useEffect(() => {
    if (!activeEnv || !status?.connected || tab !== 'overview') return;
    const run = async () => {
      setOverviewLoading(true);
      setOverviewError(null);
      try {
        const resp = await fetch(
          `/api/analytics/ga4?environmentId=${activeEnv.id}&action=overview&days=${days}`,
          { credentials: 'include' }
        );
        const data = await resp.json();
        if (!resp.ok || !data.success) {
          throw new Error(data.details || data.error || data.message || 'Failed to load overview');
        }
        setOverview({
          kpis: data.kpis || {},
          trend: data.trend || [],
          topPages: data.topPages || [],
          days: data.days || days
        });
      } catch (err: any) {
        setOverviewError(err.message);
      } finally {
        setOverviewLoading(false);
      }
    };
    run();
  }, [activeEnv?.id, status?.connected, tab, days]);

  const handlePageSearch = async () => {
    if (!activeEnv || !pageQuery.trim()) return;
    setPageLoading(true);
    setPageError(null);
    setPageResult(null);
    try {
      const resp = await fetch(
        `/api/analytics/ga4?environmentId=${activeEnv.id}&action=pageSearch&days=${days}&path=${encodeURIComponent(pageQuery.trim())}`,
        { credentials: 'include' }
      );
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.details || data.error || data.message || 'Failed to search page');
      }
      setPageResult({
        query: data.query,
        matches: data.matches || [],
        siteAverage: data.siteAverage || {},
        trend: data.trend || [],
        days: data.days || days
      });
    } catch (err: any) {
      setPageError(err.message);
    } finally {
      setPageLoading(false);
    }
  };

  const exportCsv = (rows: Array<Record<string, any>>, filename: string) => {
    if (!rows.length) {
      toast.warning('No rows to export.');
      return;
    }
    const headers = Object.keys(rows[0]);
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(','),
      ...rows.map(r => headers.map(h => escape(r[h])).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!activeEnv) return <EmptyState />;

  // Not connected state
  if (statusChecked && !status?.connected) {
    return (
      <div className="space-y-8 animate-in fade-in duration-700">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Analytics</h2>
          <p className="text-slate-500 mt-2 text-sm">Pull GA4 data for this environment.</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 flex items-start gap-4">
          <Lock size={24} className="text-amber-500 flex-shrink-0 mt-1" />
          <div className="space-y-3">
            <p className="font-bold text-amber-900">GA4 not connected for this environment.</p>
            <p className="text-sm text-amber-800">
              To pull real Analytics data, configure GA4 in one of two ways:
            </p>
            <ol className="text-sm text-amber-800 space-y-2 list-decimal list-inside">
              <li>
                <strong>In-app:</strong> Go to <a href="#/settings" className="underline font-bold">Settings</a> and connect Google Analytics 4 with your Property ID + Service Account JSON.
              </li>
              <li>
                <strong>Cloudflare env vars:</strong> set <code className="bg-white px-1.5 py-0.5 rounded text-amber-900 text-xs">GA4_PROPERTY_ID</code> (numeric, e.g. <code className="bg-white px-1.5 py-0.5 rounded text-amber-900 text-xs">123456789</code>) and <code className="bg-white px-1.5 py-0.5 rounded text-amber-900 text-xs">GA4_SERVICE_ACCOUNT_JSON</code> (full service account key JSON).
              </li>
            </ol>
            <div className="bg-white border border-amber-200 rounded-lg p-3 text-xs text-amber-900 space-y-1">
              <p className="font-bold">Don't forget:</p>
              <p>• Grant the service account email <strong>Viewer</strong> access in GA4 Admin → Property Access Management</p>
              <p>• Enable <strong>Google Analytics Data API</strong> in your Cloud project</p>
              <p>• The Measurement ID (<code>G-XXXXX</code>) is <em>not</em> an API key — it's just the browser tag</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <h2 className="text-4xl font-black text-slate-900 tracking-tight">Analytics</h2>
          <p className="text-slate-500 mt-2 text-sm">
            Live GA4 data for <span className="font-bold text-[#003B71]">{activeEnv.name}</span>
            {status?.propertyId && (
              <span className="ml-2 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-bold">
                Property {status.propertyId}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Range</label>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold focus:ring-2 focus:ring-blue-400 outline-none"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 transition-all ${
              tab === key
                ? 'border-[#003B71] text-[#003B71]'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* ========== OVERVIEW TAB ========== */}
      {tab === 'overview' && (
        <div className="space-y-8">
          {overviewLoading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={32} className="animate-spin text-blue-500" />
            </div>
          )}

          {overviewError && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3">
              <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-900">Failed to load GA4 data</p>
                <p className="text-xs text-red-700 mt-1 font-mono break-all">{overviewError}</p>
              </div>
            </div>
          )}

          {!overviewLoading && !overviewError && overview && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <KpiCard label="Active Users" value={formatNumber(overview.kpis.activeUsers)} icon={Users} color="blue" />
                <KpiCard label="Sessions" value={formatNumber(overview.kpis.sessions)} icon={MousePointerClick} color="purple" />
                <KpiCard label="Page Views" value={formatNumber(overview.kpis.screenPageViews)} icon={Eye} color="emerald" />
                <KpiCard label="Avg. Duration" value={formatDuration(overview.kpis.averageSessionDuration)} icon={Clock} color="amber" />
                <KpiCard label="Bounce Rate" value={formatPercent(overview.kpis.bounceRate)} icon={ArrowDownRight} color="rose" />
                <KpiCard label="Engagement" value={formatPercent(overview.kpis.engagementRate)} icon={TrendingUp} color="sky" />
              </div>

              {/* Trend Sparkline */}
              <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-black text-slate-900">Traffic Trend</h3>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{overview.days} days</span>
                </div>
                <div className="p-6">
                  <TrendChart points={overview.trend.map(p => ({ date: p.date, value: p.activeUsers || 0 }))} />
                </div>
              </section>

              {/* Top Pages */}
              <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-black text-slate-900">Top Pages</h3>
                  <button
                    onClick={() => exportCsv(overview.topPages, `ga4-top-pages-${overview.days}d.csv`)}
                    className="text-xs font-bold text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-all"
                  >
                    Export CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="text-left px-6 py-3">Page</th>
                        <th className="text-right px-6 py-3">Views</th>
                        <th className="text-right px-6 py-3">Users</th>
                        <th className="text-right px-6 py-3">Avg Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {overview.topPages.slice(0, 25).map((p, i) => (
                        <tr key={i} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-3">
                            <div className="font-bold text-slate-900 text-xs truncate max-w-md" title={p.pageTitle}>
                              {p.pageTitle || '(untitled)'}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono truncate max-w-md" title={p.pagePath}>
                              {p.pagePath}
                            </div>
                          </td>
                          <td className="text-right px-6 py-3 font-bold">{formatNumber(p.screenPageViews)}</td>
                          <td className="text-right px-6 py-3">{formatNumber(p.activeUsers)}</td>
                          <td className="text-right px-6 py-3 text-slate-500">{formatDuration(p.averageSessionDuration)}</td>
                        </tr>
                      ))}
                      {overview.topPages.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-slate-400 italic py-8">No data in this range.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      )}

      {/* ========== PAGE SEARCH TAB ========== */}
      {tab === 'pageSearch' && (
        <div className="space-y-8">
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-2">
              Search by URL Path
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={pageQuery}
                onChange={(e) => setPageQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handlePageSearch(); }}
                placeholder="/news/my-article-slug or partial match like 'about'"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono focus:ring-2 focus:ring-blue-400 outline-none"
              />
              <button
                onClick={handlePageSearch}
                disabled={pageLoading || !pageQuery.trim()}
                className="px-6 py-3 bg-[#003B71] text-white rounded-xl font-bold text-sm hover:bg-[#002a50] disabled:opacity-50 transition-all flex items-center gap-2"
              >
                {pageLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                Search
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2 italic">
              Partial match — enter any part of a page URL. Returns all matching pages with performance vs site average.
            </p>
          </section>

          {pageError && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3">
              <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-900">Search failed</p>
                <p className="text-xs text-red-700 mt-1 font-mono break-all">{pageError}</p>
              </div>
            </div>
          )}

          {pageResult && !pageLoading && (
            <>
              {pageResult.matches.length === 0 ? (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center">
                  <Search size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="font-bold text-slate-600">No pages matched "{pageResult.query}"</p>
                  <p className="text-xs text-slate-400 mt-1 italic">Try a shorter or different substring.</p>
                </div>
              ) : (
                <>
                  {/* Matches with comparison to average */}
                  <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="font-black text-slate-900">
                        Matches <span className="text-sm font-normal text-slate-500">({pageResult.matches.length})</span>
                      </h3>
                      <button
                        onClick={() => exportCsv(pageResult.matches, `ga4-page-search-${pageResult.days}d.csv`)}
                        className="text-xs font-bold text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-all"
                      >
                        Export CSV
                      </button>
                    </div>
                    <div className="p-6 space-y-4">
                      {pageResult.matches.map((m, i) => (
                        <MatchCard
                          key={i}
                          match={m}
                          average={pageResult.siteAverage}
                        />
                      ))}
                    </div>
                  </section>

                  {/* Trend for matched pages */}
                  {pageResult.trend.length > 0 && (
                    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-black text-slate-900">Views Over Time</h3>
                      </div>
                      <div className="p-6">
                        <TrendChart points={pageResult.trend.map(p => ({ date: p.date, value: p.screenPageViews || 0 }))} />
                      </div>
                    </section>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers + sub-components
// ---------------------------------------------------------------------------

function formatNumber(v: any): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString();
}

function formatPercent(v: any): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  // GA4 returns bounce/engagement as 0-1 range
  return `${(n * 100).toFixed(1)}%`;
}

function formatDuration(v: any): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const mins = Math.floor(n / 60);
  const secs = Math.round(n % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

const KPI_COLORS: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-600' },
  sky: { bg: 'bg-sky-50', text: 'text-sky-600' },
};

const KpiCard: React.FC<{ label: string; value: string; icon: any; color: keyof typeof KPI_COLORS }> = ({ label, value, icon: Icon, color }) => {
  const c = KPI_COLORS[color];
  return (
    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
      <div className={`inline-flex p-2 rounded-lg ${c.bg} ${c.text} mb-3`}>
        <Icon size={16} />
      </div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-xl font-black text-slate-900">{value}</p>
    </div>
  );
};

const TrendChart: React.FC<{ points: Array<{ date: string; value: number }> }> = ({ points }) => {
  if (!points.length) {
    return <p className="text-center text-slate-400 italic text-sm">No trend data.</p>;
  }
  const width = 800;
  const height = 160;
  const pad = 20;
  const values = points.map(p => p.value);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const step = (width - pad * 2) / Math.max(points.length - 1, 1);

  const coords = points.map((p, i) => {
    const x = pad + i * step;
    const y = height - pad - ((p.value - min) / range) * (height - pad * 2);
    return { x, y, ...p };
  });

  const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ');
  const areaPath = `${path} L ${coords[coords.length - 1].x} ${height - pad} L ${coords[0].x} ${height - pad} Z`;

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto min-w-[500px]">
        <defs>
          <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#003B71" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#003B71" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#trendGradient)" />
        <path d={path} fill="none" stroke="#003B71" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {coords.map((c, i) => (
          <circle key={i} cx={c.x} cy={c.y} r="2.5" fill="#003B71">
            <title>{`${c.date}: ${c.value.toLocaleString()}`}</title>
          </circle>
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-400 font-bold mt-2 px-5">
        <span>{formatDate(points[0].date)}</span>
        <span>Max: {max.toLocaleString()}</span>
        <span>{formatDate(points[points.length - 1].date)}</span>
      </div>
    </div>
  );
};

function formatDate(d: string): string {
  // GA4 returns dates as YYYYMMDD
  if (/^\d{8}$/.test(d)) {
    return `${d.slice(4, 6)}/${d.slice(6, 8)}`;
  }
  return d;
}

const MatchCard: React.FC<{ match: Record<string, any>; average: Record<string, number> }> = ({ match, average }) => {
  const compare = (metric: string) => {
    const pageVal = Number(match[metric] || 0);
    const avgVal = Number(average[metric] || 0);
    if (avgVal === 0) return { diff: 0, better: pageVal > 0 };
    const pct = ((pageVal - avgVal) / avgVal) * 100;
    return { diff: pct, better: pct > 0 };
  };

  const viewsCmp = compare('screenPageViews');

  return (
    <div className="border border-slate-100 rounded-xl p-5 hover:border-blue-200 hover:bg-slate-50/50 transition-all">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-sm text-slate-900 font-bold break-all">{match.pagePath}</p>
        </div>
        <a
          href={match.pagePath?.startsWith('http') ? match.pagePath : undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-300 hover:text-blue-500 transition-colors flex-shrink-0"
          title="Open page (only works for absolute URLs)"
        >
          <ExternalLink size={14} />
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
        <CompareStat label="Views" value={formatNumber(match.screenPageViews)} diff={viewsCmp.diff} />
        <CompareStat label="Users" value={formatNumber(match.activeUsers)} />
        <CompareStat label="Avg Time" value={formatDuration(match.averageSessionDuration)} />
        <CompareStat label="Bounce" value={formatPercent(match.bounceRate)} />
        <CompareStat label="Engagement" value={formatPercent(match.engagementRate)} />
      </div>
    </div>
  );
};

const CompareStat: React.FC<{ label: string; value: string; diff?: number }> = ({ label, value, diff }) => {
  const showDiff = diff != null && Number.isFinite(diff) && Math.abs(diff) >= 1;
  return (
    <div>
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="font-bold text-slate-900">{value}</p>
      {showDiff && (
        <p className={`text-[10px] font-bold flex items-center gap-0.5 mt-0.5 ${diff! > 0 ? 'text-green-600' : 'text-red-500'}`}>
          {diff! > 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
          {Math.abs(diff!).toFixed(0)}% vs avg
        </p>
      )}
    </div>
  );
};

export default Analytics;
